import { refreshToken } from './auth';

interface ApiRequestOptions extends RequestInit {
  requiresHouseholdId?: boolean;
  _isRetry?: boolean;
}

let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

const handleTokenRefresh = async (): Promise<void> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const storedRefreshToken = localStorage.getItem('refreshToken');

      if (!storedRefreshToken) {
        throw new Error('REFRESH_TOKEN_EXPIRED');
      }

      const data = await refreshToken(storedRefreshToken);

      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refresh_token);
      localStorage.setItem('refreshTokenExpiration', data.refresh_token_expiration.toString());
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('refreshTokenExpiration');
      localStorage.removeItem('activeHouseholdId');

      window.location.href = '/login';
      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const createApiRequest = async (endpoint: string, options: ApiRequestOptions = {}): Promise<Response> => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');
  const activeHouseholdId = localStorage.getItem('activeHouseholdId');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (options.requiresHouseholdId !== false && activeHouseholdId) {
    headers['X-Active-Household-Id'] = activeHouseholdId;
  }

  const { requiresHouseholdId, _isRetry, ...fetchOptions } = options;

  const response = await fetch(`${apiUrl}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  if (response.status === 401 && !_isRetry) {
    const errorBody = await response.clone().json().catch(() => ({}));
    const isTokenExpired =
      errorBody.message === 'Expired JWT Token' ||
      errorBody.message === 'JWT Token not found or Expired JWT Token';

    if (isTokenExpired) {
      try {
        await handleTokenRefresh();

        return createApiRequest(endpoint, { ...options, _isRetry: true });
      } catch (refreshError) {
        return response;
      }
    }
  }

  return response;
};

export const apiRequest = {
  get: (endpoint: string, options: ApiRequestOptions = {}) =>
    createApiRequest(endpoint, { ...options, method: 'GET' }),

  post: (endpoint: string, data?: any, options: ApiRequestOptions = {}) =>
    createApiRequest(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: (endpoint: string, data?: any, options: ApiRequestOptions = {}) =>
    createApiRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: (endpoint: string, data?: any, options: ApiRequestOptions = {}) =>
    createApiRequest(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: (endpoint: string, options: ApiRequestOptions = {}) =>
    createApiRequest(endpoint, { ...options, method: 'DELETE' }),
};